import { create } from 'zustand';

import { getApiBase } from '@/lib/api';
import { DEFAULT_LOCALE, isSupportedLocale, type SupportedLocale } from '@/i18n/config';
import { writeLocaleCookie } from '@/lib/i18n/locale-cookie';
import { usesBearerSession } from '@/lib/native/bearer-session';
import { ensureTokenLoaded, getNativeToken } from '@/lib/native/token';

export type LicenseType = 'PRO' | 'BUSINESS' | 'MAX' | 'KNOWLEDGE_ONLY' | 'NONE';
export type BillingStatus = 'TRIAL' | 'ACTIVE' | 'PAST_DUE' | 'SUSPENDED' | 'CANCELLED';
/**
 * Why the workspace was suspended. `billingStatus` alone cannot say it, which
 * is how a customer paying since April came to be told his trial had expired.
 */
export type SuspensionReason =
  | 'TRIAL_EXPIRED'
  | 'PAYMENT_FAILED'
  | 'SUBSCRIPTION_CANCELED'
  | 'INVOICE_UNCOLLECTIBLE'
  | 'MANUAL';
/** How the workspace pays: Stripe, an invoice we write by hand, or the App Store (KAN-10). */
export type BillingType = 'STRIPE' | 'MANUAL' | 'APPLE';

export interface UserFeatures {
  chatAllModels: boolean;
  chatKnowledgeModel: boolean;
  documentUpload: boolean;
  webSearch: boolean;
  imageGeneration: boolean;
  agents: boolean;
  toolsMcp: boolean;
  agentSkills: boolean;
  executeCode: boolean;
  knowledgeRead: boolean;
  knowledgeSuggest: boolean;
  knowledgeManage: boolean;
  i18nToggleEnabled: boolean;
}

const DEFAULT_FEATURES: UserFeatures = {
  chatAllModels: false,
  chatKnowledgeModel: false,
  documentUpload: false,
  webSearch: false,
  imageGeneration: false,
  agents: false,
  toolsMcp: false,
  agentSkills: false,
  executeCode: false,
  knowledgeRead: false,
  knowledgeSuggest: false,
  knowledgeManage: false,
  i18nToggleEnabled: false,
};

export type TrialAccess = 'FULL' | 'RESTRICTED';

/** The trial state in a /me answer: the user's, the workspace's and what it may use (KAN-1551). */
function trialStateOf(
  payload: Record<string, unknown>
): Pick<SessionProfile, 'isTrial' | 'tenantIsTrial' | 'trialAccess'> {
  return {
    isTrial: Boolean(payload['isTrial']),
    tenantIsTrial: Boolean(payload['tenantIsTrial']),
    trialAccess: payload['trialAccess'] === 'RESTRICTED' ? 'RESTRICTED' : 'FULL',
  };
}

/** A list of strings from a /me answer; anything else in it is dropped. */
function stringListOf(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : [];
}

/** What a /me answer says the user may do, and what only their plan keeps from them (KAN-1732). */
function capabilityStateOf(
  payload: Record<string, unknown>
): Pick<SessionProfile, 'capabilities' | 'planLockedCapabilities'> {
  return {
    capabilities: stringListOf(payload['capabilities']),
    planLockedCapabilities: stringListOf(payload['planLockedCapabilities']),
  };
}

export interface SessionProfile {
  sub?: string;
  email?: string;
  name?: string;
  tenantId?: string;
  identityProvider?: string;
  roles: string[];
  isSuperadmin: boolean;
  isTenantAdmin: boolean;
  isTrial: boolean;
  tenantIsTrial: boolean;
  /**
   * What the workspace may use right now (KAN-1551): `RESTRICTED` while a
   * trial still starts restricted (cheapest models only, no own MCP servers),
   * else `FULL`. Drives hints only; the server enforces it on its own.
   * Optional: absent in older payloads / test fixtures, read as `FULL`.
   */
  trialAccess?: TrialAccess;
  tenantIsPartner: boolean;
  /**
   * Own logo instead of the 9brains one. Only ever set while the workspace is
   * unlocked for it AND a logo of that variant exists, so a value means
   * "render it" — and doubles as the cache-buster in the image URL.
   */
  tenantLogoLightUpdatedAt?: string | null;
  tenantLogoDarkUpdatedAt?: string | null;
  licenseType: LicenseType;
  features: UserFeatures;
  billingStatus: BillingStatus | null;
  canSelfUpgrade: boolean;
  billingType: BillingType | null;
  trialEndsAt: string | null;
  suspendedAt: string | null;
  suspensionReason: SuspensionReason | null;
  subscriptionEndsAt: string | null;
  /**
   * Generic server-side UI marker store: ``{ markerKey: ISO-8601 timestamp }``.
   * Holds dismissible one-time hints (e.g. ``skills-intro``) and "seen up to T"
   * high-water marks (reserved key ``changelog``). Replaces the former
   * single-purpose ``changelogLastSeenAt`` field.
   */
  uiMarkers: Record<string, string>;
  locale: SupportedLocale;
  /**
   * Granted RBAC capability keys (e.g. ``agent:create``) for this user, already
   * resolved server-side (license + flag + grant, with admin bypass applied).
   * The frontend gates buttons/sections on these — never a security boundary.
   * Optional: absent in older payloads / test fixtures, read as ``?? []``.
   */
  capabilities?: string[];
  /**
   * Capabilities the plan alone keeps from this user (KAN-1732): the workspace
   * has the feature and no group right is missing, so an upgrade would unlock
   * it. Those areas preview what they bring instead of refusing.
   * Optional: absent in older payloads / test fixtures, read as ``?? []``.
   */
  planLockedCapabilities?: string[];
}

/**
 * Coarse auth state derived from the last /me bootstrap. Drives the native
 * auth gate (NativeAuthGate): 'unauthenticated' (401) shows the login screen,
 * 'unreachable' (network/CORS/5xx) shows the offline screen with retry —
 * previously both rendered the app shell in a broken half-state.
 */
export type AuthStatus = 'unknown' | 'authenticated' | 'unauthenticated' | 'unreachable';

interface SessionState {
  profile: SessionProfile | null;
  isLoading: boolean;
  hasLoaded: boolean;
  error: string | null;
  authStatus: AuthStatus;
  loadProfile: (options?: { force?: boolean }) => Promise<void>;
  setProfile: (profile: SessionProfile | null) => void;
  reset: () => void;
}

const buildProfileUrl = () => {
  const base = getApiBase();
  const path = '/api/v1/me';
  return base ? `${base}${path}` : path;
};

export const useSessionStore = create<SessionState>((set, get) => ({
  profile: null,
  isLoading: false,
  hasLoaded: false,
  error: null,
  authStatus: 'unknown',

  setProfile: (profile) =>
    set({
      profile,
      hasLoaded: true,
      isLoading: false,
      error: null,
      authStatus: profile ? 'authenticated' : 'unauthenticated',
    }),

  loadProfile: async (options) => {
    const force = options?.force ?? false;
    if (get().isLoading) return;
    if (get().hasLoaded && !force) return;

    set({ isLoading: true, error: null });
    try {
      const headers: Record<string, string> = {
        Accept: 'application/json',
      };
      if (typeof window !== 'undefined') {
        const tenantId = sessionStorage.getItem('currentTenantId');
        if (tenantId) {
          headers['X-Tenant-Id'] = tenantId;
        }
      }
      // The app and the Office pane authenticate via Bearer. This /me bootstrap
      // bypasses fetchWithAuth (raw fetch), so attach the session token explicitly.
      // Wait for the keychain load (started, unawaited, at app startup) so a
      // freshly launched app restores its session instead of bouncing to login.
      let sentNativeToken: string | null = null;
      if (usesBearerSession()) {
        await ensureTokenLoaded();
        sentNativeToken = getNativeToken();
        if (sentNativeToken) headers['Authorization'] = `Bearer ${sentNativeToken}`;
      }
      let response = await fetch(buildProfileUrl(), {
        credentials: 'include',
        headers,
      });

      // Native: the token may have been replaced by a re-login while this
      // request was in flight — retry once with the fresh token before
      // treating the 401 as "not logged in" (same pattern as fetchWithAuth).
      //
      // Failing that, renew from the stored refresh token. THIS is where the
      // lost-login shows up: the native shell gates the whole app on this one
      // request (session-provider renders the auth gate until it succeeds), so
      // nothing else runs first — a session that died with its Redis entry
      // reached the login browser from right here, every time (KAN-316).
      let renewalWasTransient = false;
      if (response.status === 401 && usesBearerSession()) {
        let retryToken = getNativeToken();
        if (!retryToken || retryToken === sentNativeToken) {
          const { renewNativeSession } = await import('@/lib/native/auth');
          const renewal = await renewNativeSession();
          retryToken = renewal.sessionId;
          renewalWasTransient = renewal.transient;
        }
        if (retryToken && retryToken !== sentNativeToken) {
          headers['Authorization'] = `Bearer ${retryToken}`;
          response = await fetch(buildProfileUrl(), {
            credentials: 'include',
            headers,
          });
        }
      }

      if (!response.ok) {
        if (response.status === 401 && renewalWasTransient) {
          // The renewal could not be asked (offline, rate limited, backend
          // down), so this 401 proves nothing about the login. Report it as
          // "cannot reach the server", which is what it is — the app keeps its
          // credentials and tries again instead of demanding a sign-in.
          set({ authStatus: 'unreachable' });
        } else if (response.status === 401) {
          set({ authStatus: 'unauthenticated' });
          // Native: auto-open the login browser ONLY when a stored token
          // existed — that is an expired session of a previously logged-in
          // user. A cold logged-out start renders the NativeAuthGate instead,
          // and the login starts from its button (no surprise popup).
          if (usesBearerSession() && sentNativeToken) {
            void import('@/lib/native/auth')
              .then((m) => m.startNativeLogin())
              .catch((err) => console.error('[native-auth] bootstrap login failed:', err));
          }
        } else {
          set({ authStatus: 'unreachable' });
        }
        throw new Error(`Profil konnte nicht geladen werden (Status ${response.status})`);
      }

      const payload = (await response.json()) as Record<string, unknown>;
      const roles = Array.isArray(payload?.['roles'])
        ? (payload['roles'] as unknown[]).filter((role): role is string => typeof role === 'string')
        : [];

      const rawFeatures = payload['features'] as Record<string, unknown> | undefined;
      const features: UserFeatures = {
        ...DEFAULT_FEATURES,
        ...(rawFeatures
          ? Object.fromEntries(
              Object.entries(rawFeatures).filter(([, v]) => typeof v === 'boolean')
            )
          : {}),
      };

      const licenseType =
        typeof payload['licenseType'] === 'string' &&
        ['PRO', 'BUSINESS', 'MAX', 'KNOWLEDGE_ONLY', 'NONE'].includes(payload['licenseType'])
          ? (payload['licenseType'] as LicenseType)
          : 'BUSINESS';

      const VALID_BILLING_STATUSES: BillingStatus[] = [
        'TRIAL',
        'ACTIVE',
        'PAST_DUE',
        'SUSPENDED',
        'CANCELLED',
      ];
      const billingStatus =
        typeof payload['billingStatus'] === 'string' &&
        VALID_BILLING_STATUSES.includes(payload['billingStatus'] as BillingStatus)
          ? (payload['billingStatus'] as BillingStatus)
          : null;

      const VALID_BILLING_TYPES: BillingType[] = ['STRIPE', 'MANUAL', 'APPLE'];
      const billingType =
        typeof payload['billingType'] === 'string' &&
        VALID_BILLING_TYPES.includes(payload['billingType'] as BillingType)
          ? (payload['billingType'] as BillingType)
          : null;

      const trialEndsAt =
        typeof payload['trialEndsAt'] === 'string' ? payload['trialEndsAt'] : null;

      const suspendedAt =
        typeof payload['suspendedAt'] === 'string' ? payload['suspendedAt'] : null;

      const VALID_SUSPENSION_REASONS: SuspensionReason[] = [
        'TRIAL_EXPIRED',
        'PAYMENT_FAILED',
        'SUBSCRIPTION_CANCELED',
        'INVOICE_UNCOLLECTIBLE',
        'MANUAL',
      ];
      const suspensionReason =
        typeof payload['suspensionReason'] === 'string' &&
        VALID_SUSPENSION_REASONS.includes(payload['suspensionReason'] as SuspensionReason)
          ? (payload['suspensionReason'] as SuspensionReason)
          : null;

      const subscriptionEndsAt =
        typeof payload['subscriptionEndsAt'] === 'string' ? payload['subscriptionEndsAt'] : null;

      const rawMarkers = payload['uiMarkers'];
      const uiMarkers: Record<string, string> =
        rawMarkers && typeof rawMarkers === 'object' && !Array.isArray(rawMarkers)
          ? Object.fromEntries(
              Object.entries(rawMarkers as Record<string, unknown>).filter(
                ([, v]) => typeof v === 'string'
              ) as [string, string][]
            )
          : {};

      const rawLocale = payload['locale'];
      const locale: SupportedLocale = isSupportedLocale(rawLocale) ? rawLocale : DEFAULT_LOCALE;
      // Mirror DB locale into the cookie so the next server render picks it up.
      writeLocaleCookie(locale);

      const profile: SessionProfile = {
        sub: typeof payload['sub'] === 'string' ? payload['sub'] : undefined,
        email: typeof payload['email'] === 'string' ? payload['email'] : undefined,
        name: typeof payload['name'] === 'string' ? payload['name'] : undefined,
        tenantId: typeof payload['tenantId'] === 'string' ? payload['tenantId'] : undefined,
        identityProvider:
          typeof payload['identityProvider'] === 'string' ? payload['identityProvider'] : undefined,
        roles,
        isSuperadmin: Boolean(payload['isSuperadmin']),
        isTenantAdmin: Boolean(payload['isTenantAdmin']),
        ...trialStateOf(payload),
        tenantIsPartner: Boolean(payload['tenantIsPartner']),
        tenantLogoLightUpdatedAt: (payload['tenantLogoLightUpdatedAt'] as string | null) ?? null,
        tenantLogoDarkUpdatedAt: (payload['tenantLogoDarkUpdatedAt'] as string | null) ?? null,
        licenseType,
        canSelfUpgrade: Boolean(payload['canSelfUpgrade']),
        features,
        billingStatus,
        billingType,
        trialEndsAt,
        suspendedAt,
        suspensionReason,
        subscriptionEndsAt,
        uiMarkers,
        locale,
        ...capabilityStateOf(payload),
      };

      set({
        profile,
        isLoading: false,
        hasLoaded: true,
        error: null,
        authStatus: 'authenticated',
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Profil konnte nicht geladen werden';
      set({
        error: message,
        profile: null,
        isLoading: false,
        hasLoaded: true,
        // The !response.ok branch already classified HTTP failures; anything
        // else landing here is a thrown fetch (network/CORS/timeout).
        ...(get().authStatus === 'authenticated' || get().authStatus === 'unknown'
          ? { authStatus: 'unreachable' as const }
          : {}),
      });
    }
  },

  reset: () => {
    set({
      profile: null,
      isLoading: false,
      hasLoaded: false,
      error: null,
      authStatus: 'unknown',
    });
  },
}));
